import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient, getSessionTokenHeaders } from '../client'
import {
  type AssessmentAnswerDto,
  type EnrollmentAssessmentDto,
  type EnglishTestDto
} from '../generated/models'
import type { AssessmentResultResponse, AssessmentSubmitRequest } from '../types/enrollment'
import { BASE_URL } from './shared'

export const useDefaultAssessmentTest = (enabled = true) => {
  return useQuery({
    queryKey: ['enrollment', 'assessment', 'default-test'],
    queryFn: async () => {
      const response = await apiClient.get<EnglishTestDto>(`${BASE_URL}/assessment/test/default`)
      return response.data
    },
    enabled
  })
}

export const useStartAssessment = () => {
  return useMutation({
    mutationFn: async (payload: { sessionToken: string; testId: number }) => {
      const response = await apiClient.post<EnrollmentAssessmentDto>(
        `${BASE_URL}/assessment/session/${encodeURIComponent(payload.sessionToken)}/start/${payload.testId}`,
        undefined,
        {
          headers: getSessionTokenHeaders(payload.sessionToken)
        }
      )

      return response.data
    }
  })
}

export const useSubmitAssessmentAnswer = () => {
  return useMutation({
    mutationFn: async ({
      sessionToken,
      assessmentId,
      data
    }: {
      sessionToken: string
      assessmentId: number
      data: AssessmentAnswerDto
    }) => {
      const response = await apiClient.post<EnrollmentAssessmentDto>(
        `${BASE_URL}/assessment/session/${encodeURIComponent(sessionToken)}/${assessmentId}/answer`,
        data,
        {
          headers: getSessionTokenHeaders(sessionToken)
        }
      )
      return response.data
    }
  })
}

export const useCompleteAssessment = () => {
  return useMutation({
    mutationFn: async ({ sessionToken, assessmentId }: { sessionToken: string; assessmentId: number }) => {
      const response = await apiClient.put<EnrollmentAssessmentDto>(
        `${BASE_URL}/assessment/session/${encodeURIComponent(sessionToken)}/${assessmentId}/complete`,
        undefined,
        {
          headers: getSessionTokenHeaders(sessionToken)
        }
      )
      return response.data
    }
  })
}

export const useSubmitAssessment = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      assessmentId,
      data
    }: {
      assessmentId: number
      data: AssessmentSubmitRequest
    }) => {
      const response = await apiClient.post<AssessmentResultResponse>(
        `${BASE_URL}/assessment/${assessmentId}/submit`,
        data
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enrollment'] })
    }
  })
}
